import { useMemo, useState } from 'react'
import { UserPlus, Users } from 'lucide-react'
import {
  Button,
  Chip,
  EmptyState,
  FilterToolbar,
  SearchField,
  StatusBadge,
} from '@shared/design-system/ui'
import { useMediaQuery } from '@shared/lib/use-media-query'
import {
  accessStatusOrder,
  enrollmentOriginLabels,
  formatMembersDate,
  getAccessStatusPresentation,
  revocationReasonLabels,
} from '../model/members-area.presentation'
import type { MembersListItem } from '../model/members-access.contract'
import type { AccessStatus } from '../model/members-area.types'
import {
  filterControls,
  mainCell,
  mobileCard,
  mobileCardHeader,
  mobileList,
  primary,
  resultCount,
  row,
  searchSlot,
  secondary,
  statusGroup,
  statusOption,
  table,
  tableWrap,
  td,
  th,
} from './members-area.css'

function AccessBadge({ student }: { student: MembersListItem }) {
  const presentation = getAccessStatusPresentation(student.accessStatus)
  const Icon = presentation.icon
  return (
    <span className={mainCell}>
      <StatusBadge
        tone={presentation.tone}
        label={presentation.label}
        icon={<Icon size={13} />}
        size="sm"
      />
      {student.accessStatus === 'REVOKED' && student.revocationReason ? (
        <span className={secondary}>{revocationReasonLabels[student.revocationReason]}</span>
      ) : null}
    </span>
  )
}

export function StudentsPanel({
  students,
  onAdd,
  onOpen,
}: {
  students: MembersListItem[]
  onAdd: () => void
  onOpen: (student: MembersListItem) => void
}) {
  const [query, setQuery] = useState('')
  const [status, setStatus] = useState<AccessStatus | undefined>()
  const isMobile = useMediaQuery('(max-width: 720px)')

  const visibleStudents = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return students.filter((student) => {
      if (status && student.accessStatus !== status) return false
      if (!normalized) return true
      return `${student.name} ${student.email}`.toLowerCase().includes(normalized)
    })
  }, [students, query, status])

  function clearFilters() {
    setQuery('')
    setStatus(undefined)
  }

  if (students.length === 0) {
    return (
      <EmptyState
        data-testid="members-students-empty"
        icon={<Users size={22} />}
        title="Nenhum membro ainda"
        description="Quem comprar um produto com área de membros aparece aqui automaticamente. Você também pode adicionar uma cortesia."
        action={
          <Button leftIcon={<UserPlus size={17} />} onClick={onAdd}>
            Adicionar cortesia
          </Button>
        }
      />
    )
  }

  const hasFilters = Boolean(query || status)

  return (
    <>
      <FilterToolbar
        chips={
          hasFilters ? (
            <>
              {status ? (
                <Chip
                  label={getAccessStatusPresentation(status).label}
                  onRemove={() => setStatus(undefined)}
                />
              ) : null}
              {query ? <Chip label={`Busca: ${query}`} onRemove={() => setQuery('')} /> : null}
            </>
          ) : null
        }
        onClear={hasFilters ? clearFilters : undefined}
        resultCount={
          <span className={resultCount}>
            {visibleStudents.length} {visibleStudents.length === 1 ? 'membro' : 'membros'}
          </span>
        }
      >
        <div className={filterControls}>
          <div className={searchSlot}>
            <SearchField
              value={query}
              onChange={setQuery}
              placeholder="Buscar por nome ou e-mail"
              ariaLabel="Buscar membros"
            />
          </div>
          <div className={statusGroup} role="group" aria-label="Filtrar membros por acesso">
            <button
              type="button"
              className={statusOption({ active: status === undefined })}
              aria-pressed={status === undefined}
              onClick={() => setStatus(undefined)}
            >
              Todos
            </button>
            {accessStatusOrder.map((option) => (
              <button
                key={option}
                type="button"
                className={statusOption({ active: status === option })}
                aria-pressed={status === option}
                onClick={() => setStatus(status === option ? undefined : option)}
              >
                {getAccessStatusPresentation(option).label}
              </button>
            ))}
          </div>
        </div>
      </FilterToolbar>

      {visibleStudents.length === 0 ? (
        <EmptyState
          data-testid="members-students-filter-empty"
          icon={<Users size={22} />}
          title="Nada neste filtro"
          description="Nenhum membro corresponde aos filtros atuais."
          action={
            <Button variant="secondary" onClick={clearFilters}>
              Limpar filtros
            </Button>
          }
        />
      ) : isMobile ? (
        <div className={mobileList} data-testid="members-students-list">
          {visibleStudents.map((student) => (
            <button
              key={student.id}
              type="button"
              className={mobileCard}
              aria-label={`Ver membro ${student.name}`}
              onClick={() => onOpen(student)}
            >
              <span className={mobileCardHeader}>
                <span className={mainCell}>
                  <span className={primary}>{student.name}</span>
                  <span className={secondary}>{student.email}</span>
                </span>
                <AccessBadge student={student} />
              </span>
              <span className={secondary}>{student.productNames.join(', ')}</span>
              <span className={secondary}>{enrollmentOriginLabels[student.origin]}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className={tableWrap}>
          <table className={table} data-testid="members-students-table">
            <thead>
              <tr>
                <th className={th}>Membro</th>
                <th className={th}>Acesso</th>
                <th className={th}>Produtos</th>
                <th className={th}>Origem</th>
                <th className={th}>Entrada</th>
              </tr>
            </thead>
            <tbody>
              {visibleStudents.map((student) => (
                <tr
                  key={student.id}
                  className={row}
                  tabIndex={0}
                  aria-label={`Ver membro ${student.name}`}
                  onClick={() => onOpen(student)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onOpen(student)
                    }
                  }}
                >
                  <td className={td}>
                    <span className={mainCell}>
                      <span className={primary}>{student.name}</span>
                      <span className={secondary}>{student.email}</span>
                    </span>
                  </td>
                  <td className={td}>
                    <AccessBadge student={student} />
                  </td>
                  <td className={td}>{student.productNames.join(', ')}</td>
                  <td className={td}>{enrollmentOriginLabels[student.origin]}</td>
                  <td className={td}>{formatMembersDate(student.enrolledAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
